/**
 * AP Invoices Demo — Nami scripted prompts + tool registry.
 *
 * THROWAWAY DEMO CODE. Mirrors the AR demo's deterministic chat spine: a small
 * set of prompts map to (a) streamed narration and (b) named frontend tools the
 * invoices page registers. The chat interceptor matches a user message, bypasses
 * the real RPC, streams the narration, and fires the named tools.
 *
 * The "pay all matched invoices" flow is two turns: the first asks the user
 * whether to pay now or follow the schedule; the second (their answer) performs
 * the payment and opens the remittance email.
 */

/** Tool registry — the invoices-page registrar fills this so the interceptor
 *  can invoke a tool by name regardless of render timing. */
const toolRegistry = new Map<string, () => unknown | Promise<unknown>>();

export function registerApDemoTool(
  name: string,
  fn: () => unknown | Promise<unknown>,
): void {
  toolRegistry.set(name, fn);
}

export function getApDemoTool(
  name: string,
): (() => unknown | Promise<unknown>) | undefined {
  return toolRegistry.get(name);
}

export interface ApDemoScript {
  prompts: string[];
  intent?: string[][];
  narration: string;
  toolNames: string[];
}

/**
 * Nami's reply when building the AP processing pipeline. Shared by the scripted
 * chat beat below and the create-workflow "describe it" prompt
 * (`useApWorkflowDescribeAutostart`), so both turns read identically.
 */
export const AP_BUILD_WORKFLOW_NARRATION =
  "Sure — I'll build an AP invoice processing pipeline: it ingests the invoice email, classifies and OCRs the attachments, extracts the invoice fields, creates a draft, fetches the PO from Famous, and matches the line items. Building it now.";

export const AP_DEMO_SCRIPTS: ApDemoScript[] = [
  {
    // Invoice detail — show this supplier's recent invoice history + trend.
    prompts: [
      'show me the last invoices',
      'show me the last 5 invoices',
      'show me the last few invoices',
      'show me the last invoices from this supplier',
      'show me recent invoices from this supplier',
      'show me the invoice history for this supplier',
      'show me this supplier invoice history',
      'compare this to previous invoices',
      'compare this invoice to previous invoices',
      'how does this invoice compare to past invoices',
      'last invoices from this supplier',
    ],
    intent: [
      ['last', 'recent', 'previous', 'past', 'history', 'compare', 'prior'],
      ['invoice', 'invoices', 'bill', 'bills'],
    ],
    narration:
      'Here are the last 6 invoices from MRO Supply Co. Their recent run rate is about $6,690, but this invoice — AIG-90412 at $9,800 — is roughly 46% above that average. The prior five had been flat around $6–7K, so this one stands out. I’ve opened the comparison so you can see the per-invoice breakdown and the trend.',
    toolNames: ['ap_show_supplier_history'],
  },
  {
    // Beat 1 — the ask.
    prompts: [
      'pay all matched invoices',
      'pay all the matched invoices',
      'pay matched invoices',
      'pay all matched',
    ],
    intent: [
      ['pay', 'settle'],
      ['matched', 'ready', 'approved', 'all'],
      ['invoice', 'invoices', 'bills'],
    ],
    narration:
      "I found 3 matched invoices ready to pay, totaling $15,250.00 — AIG-90412 (eligible for a 2% early-pay discount), the SF-3398 freight invoice, and BS-22188. Should I pay them immediately, or follow each invoice's scheduled payment date?",
    toolNames: [],
  },
  {
    // Beat 2a — pay now.
    prompts: [
      'pay immediately',
      'pay them immediately',
      'pay now',
      'pay them now',
      'immediately',
    ],
    intent: [['immediately', 'now', 'today', 'immediate']],
    narration:
      "Done — I've paid all 3 matched invoices today via ACH, capturing the $196.00 early-pay discount on AIG-90412 ($9,604.00 instead of $9,800.00). Here are the remittances that went out — one per supplier.",
    toolNames: ['ap_pay_invoices_now'],
  },
  {
    // Beat 2b — follow the schedule.
    prompts: [
      'follow the schedule',
      'follow the scheduled dates',
      'follow schedule',
      'use the scheduled dates',
      'follow the scheduled payment dates',
      'scheduled',
    ],
    intent: [['schedule', 'scheduled', 'dates', 'date']],
    narration:
      "Done — I've scheduled all 3 matched invoices on their planned payment dates (AIG-90412 still captures its 2% early-pay discount). Here are the remittances each supplier will receive.",
    toolNames: ['ap_pay_invoices_scheduled'],
  },
  {
    // Workflows list — build the AP invoice processing pipeline (no confidence
    // gate yet; that's a follow-up beat).
    prompts: [
      'make me an ap processing workflow',
      'make me an ap invoice workflow',
      'make me an ap invoice processing workflow',
      'build me an ap processing workflow',
      'build an ap invoice processing workflow',
      'create an ap processing workflow',
      'make an ap workflow',
    ],
    intent: [
      ['make', 'build', 'create', 'setup', 'set'],
      ['ap', 'invoice', 'invoices', 'payable', 'payables'],
      ['workflow', 'pipeline', 'processing', 'process'],
    ],
    narration: AP_BUILD_WORKFLOW_NARRATION,
    toolNames: ['ap_build_processing_workflow'],
  },
  {
    // Workflows list — append the auto-accept confidence gate (second turn).
    prompts: [
      'auto accept invoices with match confidence over 99',
      'auto accept invoices when confidence is over 99',
      'auto accept invoices over 99 percent confidence',
      'automatically accept high confidence invoices',
      'auto approve invoices with confidence over 99',
      'auto accept high confidence invoices',
      'add a confidence threshold',
    ],
    intent: [
      ['auto', 'automatically', 'automatic'],
      ['accept', 'approve', 'confidence', 'threshold'],
    ],
    narration:
      "Done — I've added a confidence gate at the end: invoices that match at 99% or higher are auto-accepted, and anything below 99% routes to a person for review.",
    toolNames: ['ap_add_confidence_step'],
  },
];

function normalize(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function scoreIntent(tokens: Set<string>, groups: string[][]): number | null {
  let total = 0;
  for (const group of groups) {
    const hits = group.filter((syn) => tokens.has(syn)).length;
    if (hits === 0) return null;
    total += hits;
  }
  return total;
}

/** Find the scripted beat for a user message, or null if none matches. */
export function matchApDemoScript(message: string): ApDemoScript | null {
  const norm = normalize(message);
  if (!norm) return null;

  for (const script of AP_DEMO_SCRIPTS) {
    for (const prompt of script.prompts) {
      const np = normalize(prompt);
      if (norm === np) return script;
      const words = np.split(' ');
      if (words.length > 1 && words.every((w) => norm.includes(w))) {
        return script;
      }
    }
  }

  const tokens = new Set(norm.split(' '));
  let best: ApDemoScript | null = null;
  let bestScore = 0;
  for (const script of AP_DEMO_SCRIPTS) {
    if (!script.intent) continue;
    const score = scoreIntent(tokens, script.intent);
    if (score !== null && score > bestScore) {
      best = script;
      bestScore = score;
    }
  }
  return best;
}
